import {
    activeUser,
    changePassword,
    deleteUser,
    getCompanyFromUUID,
    login,
    recoveryEmail,
    refresh,
    register,
    updateUserPassword,
    userInfo,
    validatePromotionalToken,
    validateRecoveryToken,
} from "@/api/security";
import { getSession, logoutAndClean, setSession } from "@/utils/auth";
import { readStringFlag, writeStringFlag } from "@/utils/localFlag";

const ACTIVE_COMPANY_KEY = "activeCompanyId";

export default {
    // state como factory: forward-compatible con Vuex 4 / Pinia.
    state: () => ({
        user: {},
        token: "",
        company: {},
        recoveryToken: "",
        validToken: null,
        promotionalToken: "",
        validPromotionalToken: null,
        hasSIIAutomation: false,
    }),

    mutations: {
        SET_LOGIN: (state, payload) => {
            state.user = payload.user;
            state.token = payload.token;
            state.company = payload.user.companies[0];
        },
        SET_USER_INFO: (state, payload) => {
            state.user = payload;
        },
        SET_RECOVERY_TOKEN: (state, payload) => {
            state.recoveryToken = payload;
        },
        SET_PROMOTIONAL_TOKEN: (state, payload) => {
            state.promotionalToken = payload;
        },
        SET_SII_AUTOMATION: (state, payload) => {
            state.hasSIIAutomation = payload;
        },
        SET_COMPANY: (state, payload) => {
            state.company = payload;
        },
        SET_VALID_TOKEN: (state, payload) => {
            state.validToken = payload;
        },
        SET_VALID_PROMOTIONAL_TOKEN: (state, payload) => {
            state.validPromotionalToken = payload;
        },
        CLEAR_USER: (state) => {
            state.user = "";
            state.token = "";
            state.company = "";
        },
    },

    actions: {
        loginByUserInfo({ commit }, userInfo) {
            return new Promise((resolve, reject) => {
                login(userInfo.username, userInfo.password)
                    .then((response) => {
                        const data = response.data;
                        setSession(data);
                        commit("SET_LOGIN", data);
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // ── Registro (RegisterOnboarding / RegisterChita / RegisterAnother) ──
        // Pass-throughs: devuelven la data y dejan propagar el error — cada
        // vista de registro conserva su manejo actual (consoleHandlerError).
        async registerUser(_ctx, payload) {
            return (await register(payload)).data;
        },
        async fetchCompanyFromUUID(_ctx, uuid) {
            return (await getCompanyFromUUID(uuid)).data;
        },
        // Reemite el JWT de la sesión vigente y devuelve la data. NO persiste
        // el token nuevo: su consumer (DashboardBasic) descarta el resultado
        // hoy — persistir la sesión queda en manos del caller con la data
        // devuelta. `config` permite pasar { skipAuthRedirect: true }.
        async refreshSession(_ctx, config = {}) {
            const session = getSession();
            return (await refresh(session.token, config)).data;
        },
        // ── Pass-throughs de componentes (mismo criterio que registerUser) ──
        // Devuelven la data y dejan propagar el error — cada componente
        // conserva su manejo actual (consoleHandlerError / mensajes propios).
        // Check de cuenta existente por email (RegisterPersonalDataStep).
        async checkActiveUser(_ctx, email) {
            return (await activeUser(email)).data;
        },
        // Solicita el mail de recuperación de clave (FormRecoveryPassword).
        async sendRecoveryEmail(_ctx, email) {
            return (await recoveryEmail(email)).data;
        },
        // Confirma la nueva clave con el token de recuperación (FormChangePassword).
        async changePassword(_ctx, payload) {
            return (await changePassword(payload)).data;
        },
        // Back-office: cambia credenciales de otro usuario (ChangeUsernamePass).
        async updateUserPassword(_ctx, userData) {
            return (await updateUserPassword(userData)).data;
        },
        setLoginInfo({ commit }, loginInfo) {
            commit("SET_LOGIN", loginInfo);
            if (loginInfo.user.companies[0]?.company_product_link_sii) {
                commit("SET_SII_AUTOMATION", true);
            } else {
                commit("SET_SII_AUTOMATION", false);
            }
        },
        // Payload objeto: las actions de Vuex reciben UN solo payload — la
        // firma posicional anterior (email, company_id) hacía que company_id
        // nunca llegara (dispatch lo trataba como `options`).
        // Reintenta con backoff ante fallas transitorias (timeout, 5xx, red):
        // sin esto, una sola request perdida dejaba user_info_loaded en false
        // para toda la sesión SPA (sidebar en skeleton hasta un F5 manual).
        // 4xx no se reintenta — es determinístico y el interceptor de api.js
        // ya maneja 401/403. Resuelve true/false en vez de rechazar para no
        // generar unhandled rejections en callers fire-and-forget (App.vue).
        async getUserInfo({ commit }, { email, companyId = null, retryDelaysMs = [1000, 3000] }) {
            for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
                try {
                    const response = await userInfo(email, companyId);
                    commit("SET_USER_INFO", response.data);
                    return true;
                } catch (error) {
                    const status = error?.response?.status;
                    const retriable = !status || status >= 500;
                    if (!retriable || attempt === retryDelaysMs.length) {
                        return false;
                    }
                    await new Promise((resolve) =>
                        setTimeout(resolve, retryDelaysMs[attempt])
                    );
                }
            }
        },
        // Cambia la empresa activa: persiste la selección y recarga. El reload
        // total es la estrategia v1 (decisión §8 del audit) — cada módulo Vuex
        // con datos por-empresa se re-fetchea contra la nueva empresa sin una
        // cascada de resets manual. El interceptor de api.js lee
        // `activeCompanyId` de localStorage, así que el header X-Active-Company
        // ya viaja correcto tras la recarga.
        setActiveCompany({ commit }, company) {
            if (!company || company.id == null) return;
            writeStringFlag(ACTIVE_COMPANY_KEY, String(company.id));
            commit("SET_COMPANY", company);
            // El reload conserva la URL. Si la ruta actual está anclada a una
            // empresa EXPLÍCITA (ficha del analista: /info-company?companyId=X),
            // recargarla tal cual dejaría la ficha mostrando X con el selector
            // y el header X-Active-Company en la nueva Y — pantalla mixta. En
            // ese caso se vuelve al dashboard; el resto conserva sección/tab.
            if (/[?&](companyId|company_id)=/.test(window.location.hash || "")) {
                window.location.hash = "#/dashboard";
            }
            window.location.reload();
        },
        // Restaura la empresa activa persistida tras un page-load: si está en
        // las empresas del usuario, la setea como activa (para el selector).
        // NO borra el flag cuando no hay match: la lista de companies puede
        // estar momentáneamente incompleta (p.ej. payload de login viejo antes
        // de getUserInfo), y borrar acá dejaría al interceptor sin header →
        // get_company_user caería siempre a la empresa de menor id. El backend
        // ya valida la pertenencia del header (cae a la de menor id si es
        // inválido, nunca filtra datos de otra empresa), así que un flag stale
        // es inocuo. Idempotente: se puede llamar antes y después de getUserInfo.
        restoreActiveCompany({ commit, state }) {
            const savedId = readStringFlag(ACTIVE_COMPANY_KEY);
            const companies = state.user?.companies || [];
            if (!savedId) {
                // Sembrar el flag con la empresa MOSTRADA: el logout borra
                // activeCompanyId y nada lo re-escribía hasta tocar el
                // selector — los requests iban SIN X-Active-Company y el
                // backend caía a la empresa de menor id (que puede no ser la
                // del selector) → reportes/balance de otra empresa (en ceros)
                // hasta cambiar de empresa y volver.
                if (companies.length > 0) {
                    writeStringFlag(ACTIVE_COMPANY_KEY, String(companies[0].id));
                    commit("SET_COMPANY", companies[0]);
                }
                return;
            }
            const match = companies.find(
                (c) => String(c.id) === String(savedId)
            );
            if (match) {
                commit("SET_COMPANY", match);
            }
        },
        async clearUserInfo({ commit }) {
            // La navegación a "/" la hace el caller (vista/componente); el store
            // sólo limpia la sesión. Mantiene al router fuera de la capa de estado.
            try {
                await logoutAndClean();
            } finally {
                commit("CLEAR_USER");
            }
        },
        validateRecoveryToken({ commit }, token) {
            return new Promise((resolve, reject) => {
                validateRecoveryToken(token)
                    .then(() => {
                        commit("SET_VALID_TOKEN", true);
                        resolve(true);
                    })
                    .catch(() => {
                        commit("SET_VALID_TOKEN", false);
                        reject(false);
                    });
            });
        },
        callValidatePromotionalToken({ commit }, token) {
            return new Promise((resolve, reject) => {
                validatePromotionalToken(token)
                    .then(() => {
                        commit("SET_VALID_PROMOTIONAL_TOKEN", true);
                        resolve(true);
                    })
                    .catch(() => {
                        commit("SET_VALID_PROMOTIONAL_TOKEN", false);
                        reject(false);
                    });
            });
        },
        async deleteSelf({ commit }) {
            // Igual que clearUserInfo: la navegación queda en el caller.
            try {
                await deleteUser();
                await logoutAndClean();
            } finally {
                commit("CLEAR_USER");
            }
        },
    },
    namespaced: true,
};
